import { BACKEND_URL } from '../config/backendUrl';
/**
 * TrustIdIntegrationSection — Settings → Integrations card for the TrustID
 * Right to Work / ID verification API.
 *
 * Lets a tenant admin:
 *   1. Toggle the integration on/off
 *   2. Pick environment (Sandbox / Production)
 *   3. Enter their own TrustID username + password (stored encrypted server-side)
 *   4. Test the connection (a real login+logout — TrustID's bare
 *      testConnection endpoint doesn't check credentials at all)
 *   5. Pick which Bullhorn Credential category a passed check writes to
 *
 * The password is never read back to the browser — only a masked preview.
 */
import { useEffect, useState } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import {
  ShieldCheck,
  CheckCircle2,
  XCircle,
  AlertCircle,
  RefreshCw,
  Eye,
  EyeOff,
} from 'lucide-react';
import axios from 'axios';

const API_URL = `${BACKEND_URL}/api`;

export default function TrustIdIntegrationSection() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [config, setConfig] = useState({
    enabled: false,
    environment: 'sandbox',
    username: null,
    password_set: false,
    password_preview: null,
    bullhorn_credential_category_id: null,
    bullhorn_credential_category_name: null,
    is_connected: false,
    last_test_at: null,
    last_test_ok: false,
    last_test_error: null,
  });
  const [usernameDraft, setUsernameDraft] = useState('');
  const [passwordDraft, setPasswordDraft] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [categories, setCategories] = useState([]);
  const [loadingCategories, setLoadingCategories] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await axios.get(`${API_URL}/trustid/config`);
        if (!alive) return;
        setConfig((prev) => ({ ...prev, ...data }));
        setUsernameDraft(data.username || '');
      } catch (e) {
        if (alive) toast.error('Could not load TrustID settings');
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    if (!config.enabled) return;
    let alive = true;
    setLoadingCategories(true);
    axios
      .get(`${API_URL}/trustid/bullhorn-categories`)
      .then(({ data }) => { if (alive) setCategories(data?.categories || []); })
      .catch(() => { if (alive) setCategories([]); })
      .finally(() => { if (alive) setLoadingCategories(false); });
    return () => { alive = false; };
  }, [config.enabled]);

  const saveField = async (patch) => {
    setSaving(true);
    try {
      const { data } = await axios.put(`${API_URL}/trustid/config`, patch);
      setConfig((prev) => ({ ...prev, ...data }));
      toast.success('TrustID settings saved');
      if ('password' in patch) setPasswordDraft('');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveCredentials = async () => {
    if (!usernameDraft.trim() || !passwordDraft.trim()) {
      toast.error('Enter both a username and password');
      return;
    }
    await saveField({ username: usernameDraft.trim(), password: passwordDraft.trim() });
  };

  const handleTestConnection = async () => {
    setTesting(true);
    try {
      const { data } = await axios.post(`${API_URL}/trustid/test-connection`);
      if (data?.ok) toast.success(data.message || 'TrustID connection OK');
      else toast.error(`TrustID: ${data?.message || 'Connection failed'}`);
      setConfig((prev) => ({ ...prev, is_connected: data?.ok, last_test_ok: data?.ok, last_test_error: data?.ok ? null : data?.message }));
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Test connection failed');
    } finally {
      setTesting(false);
    }
  };

  if (loading) {
    return (
      <Card className="neumorphic" data-testid="trustid-integration-card">
        <CardContent className="p-6">
          <div className="flex items-center justify-center text-muted-foreground">
            <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
            Loading TrustID settings…
          </div>
        </CardContent>
      </Card>
    );
  }

  const connected = config.password_set && config.enabled && config.is_connected;

  return (
    <div className="neumorphic rounded-xl p-6" data-testid="trustid-integration-card">
      <div className="flex items-center gap-4 mb-6">
        <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-emerald-600 to-teal-700 flex items-center justify-center">
          <ShieldCheck className="w-6 h-6 text-white" />
        </div>
        <div className="flex-1">
          <h2 className="font-heading font-semibold text-lg">TrustID Right to Work Check</h2>
          <p className="text-sm text-muted-foreground">
            Runs a Right to Work / ID verification as a packet step via TrustID's Guest
            Link — the candidate completes a document scan + liveness check on TrustID's
            own site, and a passing result writes back to Bullhorn as a Credential.
          </p>
        </div>
        {connected ? (
          <div className="flex items-center gap-2 px-3 py-1.5 bg-emerald-50 rounded-full" data-testid="trustid-status-connected">
            <CheckCircle2 className="w-4 h-4 text-emerald-600" />
            <span className="text-sm font-medium text-emerald-700">Active</span>
          </div>
        ) : config.password_set ? (
          <div className="flex items-center gap-2 px-3 py-1.5 bg-amber-50 rounded-full">
            <AlertCircle className="w-4 h-4 text-amber-600" />
            <span className="text-sm font-medium text-amber-700">Not tested / disabled</span>
          </div>
        ) : (
          <div className="flex items-center gap-2 px-3 py-1.5 bg-slate-100 rounded-full">
            <XCircle className="w-4 h-4 text-slate-500" />
            <span className="text-sm font-medium text-slate-600">Not configured</span>
          </div>
        )}
      </div>

      <div className="flex items-center justify-between py-3 border-b border-slate-200">
        <div>
          <Label className="text-sm font-medium">Enable TrustID for this workspace</Label>
          <p className="text-xs text-muted-foreground mt-0.5">
            When OFF, the Right to Work check step can't be added to new packets and
            candidates already on an in-progress check can't start it.
          </p>
        </div>
        <Switch
          checked={!!config.enabled}
          onCheckedChange={(v) => saveField({ enabled: v })}
          disabled={saving}
          data-testid="trustid-enable-switch"
        />
      </div>

      <div className="py-3 border-b border-slate-200">
        <Label className="text-sm font-medium">Environment</Label>
        <div className="mt-2 flex gap-2">
          {[
            { key: 'sandbox', label: 'Sandbox', hint: 'sandbox.trustid.co.uk' },
            { key: 'production', label: 'Production', hint: 'cloud.trustid.co.uk' },
          ].map((env) => {
            const active = config.environment === env.key;
            return (
              <button
                key={env.key}
                type="button"
                disabled={saving}
                onClick={() => saveField({ environment: env.key })}
                className={`flex-1 px-3 py-2 rounded-lg border text-left transition-colors ${
                  active ? 'border-emerald-500 bg-emerald-50' : 'border-slate-200 hover:border-slate-300 bg-white'
                }`}
                data-testid={`trustid-env-${env.key}`}
              >
                <div className="text-sm font-medium">{env.label}</div>
                <div className="text-[11px] text-muted-foreground font-mono">{env.hint}</div>
              </button>
            );
          })}
        </div>
      </div>

      <div className="py-3 border-b border-slate-200">
        <Label className="text-sm font-medium">TrustID Credentials</Label>
        <p className="text-xs text-muted-foreground mt-0.5 mb-2">
          Your own TrustID Cloud username and password. Stored encrypted; the password is
          never returned in plain text.
        </p>
        <div className="space-y-2">
          <Input
            type="text"
            placeholder="TrustID username"
            value={usernameDraft}
            onChange={(e) => setUsernameDraft(e.target.value)}
            className="text-sm"
            data-testid="trustid-username-input"
          />
          <div className="flex items-center gap-2">
            <div className="relative flex-1">
              <Input
                type={showPassword ? 'text' : 'password'}
                placeholder={config.password_set ? `Saved (${config.password_preview || '••••••••'}) — enter a new one to replace` : 'TrustID password'}
                value={passwordDraft}
                onChange={(e) => setPasswordDraft(e.target.value)}
                className="pr-9 text-sm"
                data-testid="trustid-password-input"
              />
              <button
                type="button"
                onClick={() => setShowPassword((s) => !s)}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                tabIndex={-1}
              >
                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
            <Button
              size="sm"
              onClick={handleSaveCredentials}
              disabled={saving || !usernameDraft.trim() || !passwordDraft.trim()}
              data-testid="trustid-save-credentials-btn"
            >
              Save
            </Button>
          </div>
        </div>
        {config.password_set && (
          <div className="flex items-center gap-2 mt-3">
            <Button
              size="sm"
              variant="outline"
              onClick={handleTestConnection}
              disabled={testing}
              data-testid="trustid-test-btn"
            >
              {testing ? (<><RefreshCw className="w-3.5 h-3.5 mr-1.5 animate-spin" /> Testing…</>) : 'Test connection'}
            </Button>
            {config.last_test_at && (
              <span className={`text-xs ${config.last_test_ok ? 'text-emerald-700' : 'text-red-700'}`}>
                {config.last_test_ok ? '✓ ' : '✗ '}
                {config.last_test_ok ? 'Connected' : (config.last_test_error || 'Connection failed')}
              </span>
            )}
          </div>
        )}
      </div>

      <div className="pt-3">
        <Label className="text-sm font-medium">Bullhorn Credential Category</Label>
        <p className="text-xs text-muted-foreground mt-0.5 mb-2">
          Which Bullhorn Candidate Credential category a passed check is written to (with the
          expiry date and PDF report attached).
        </p>
        {loadingCategories ? (
          <div className="text-xs text-muted-foreground flex items-center">
            <RefreshCw className="w-3 h-3 mr-1.5 animate-spin" />
            Fetching categories from Bullhorn…
          </div>
        ) : categories.length > 0 ? (
          <select
            className="w-full px-3 py-2 rounded-lg border border-slate-200 bg-white text-sm"
            value={config.bullhorn_credential_category_id || ''}
            onChange={(e) => {
              const selected = categories.find((cat) => String(cat.id) === e.target.value);
              saveField({ bullhorn_credential_category_id: e.target.value, bullhorn_credential_category_name: selected?.name || '' });
            }}
            disabled={saving}
            data-testid="trustid-category-select"
          >
            <option value="">— Select a category —</option>
            {categories.map((cat) => (
              <option key={cat.id} value={cat.id}>{cat.name}</option>
            ))}
          </select>
        ) : (
          <p className="text-[11px] text-muted-foreground">
            {config.enabled
              ? "Couldn't fetch Bullhorn categories — check your Bullhorn connection in Settings."
              : 'Enable TrustID above to load Bullhorn categories.'}
          </p>
        )}
      </div>
    </div>
  );
}
